import { useRouterParams } from "@zapier/shared";
import { SortDescriptor, SortDirection } from "@zapier/design-system";

export enum EmptyConnectionsTable {
  EmptySearchResults = "EmptySearchResults",
  NoSharedConnections = "NoSharedConnections",
  NoOwnedConnections = "NoOwnedConnections",
}

interface Input {
  isLoading?: boolean;
  hasNoConnections?: boolean;
  sortDescriptor?: SortDescriptor;
  // Total connections across all pages. When 0, confirms a "no owned
  // connections" result isn't just a stale/out-of-range page param.
  totalCount?: number;
}

export function useShowEmptyConnectionsTable({
  isLoading,
  hasNoConnections,
  sortDescriptor,
  totalCount,
}: Input) {
  const {
    app,
    apps,
    connection,
    owner,
    owners,
    ownerStatus,
    status,
    shared,
    managedAuthViolation,
    order,
    search,
  } = useRouterParams();

  const isSearch = !!search;
  const isSharedWithMe = shared === "-me";
  // totalCount reflects active filters, not the whole account — any filter
  // besides "shared with me" must count as a filtered-empty result.
  const hasOtherActiveFilter = !!(
    isSearch ||
    app ||
    apps ||
    connection ||
    owner ||
    owners ||
    ownerStatus ||
    status ||
    managedAuthViolation ||
    (shared && !isSharedWithMe)
  );

  // update sortDescriptor if order is provided
  if (order && sortDescriptor) {
    sortDescriptor.column = order.replace("-", "");
    sortDescriptor.direction = (
      order[0] === "-" ? "descending" : "ascending"
    ) as SortDirection;
  }

  /* Empty Search */
  if (hasOtherActiveFilter && hasNoConnections && !isLoading) {
    return EmptyConnectionsTable.EmptySearchResults;
  }

  /* Empty Shared List */
  if (
    !hasOtherActiveFilter &&
    hasNoConnections &&
    !isLoading &&
    isSharedWithMe
  ) {
    return EmptyConnectionsTable.NoSharedConnections;
  }

  /* Empty List */
  if (
    !hasOtherActiveFilter &&
    !isSharedWithMe &&
    hasNoConnections &&
    !isLoading &&
    (totalCount === undefined || totalCount === 0)
  ) {
    return EmptyConnectionsTable.NoOwnedConnections;
  }

  return;
}
